// src/components/admin/WorksheetManager.tsx
// Komponen admin untuk mengelola worksheet

import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Download, Search, Filter } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from './Toast';
import { ImageUpload } from './ImageUpload';
import { FileUpload } from './FileUpload';
import { useSimpleProfileData } from '../../hooks/useProfileData';

interface Worksheet {
  id?: string;
  profile_id?: string;
  title: string;
  description?: string;
  category: string;
  age_range: string;
  difficulty_level: string;
  file_url?: string;
  preview_image_url?: string;
  file_size?: string;
  page_count?: number;
  download_count: number;
  rating: number;
  is_featured: boolean;
  is_active: boolean;
  sort_order: number;
}

const categories = [
  'Matematika',
  'Bahasa Indonesia',
  'Bahasa Inggris',
  'Sains',
  'Seni',
  'Olahraga',
  'Umum'
];

const ageRanges = [
  '3-5 tahun',
  '6-8 tahun',
  '9-12 tahun',
  '13-15 tahun',
  '16+ tahun'
];

const difficultyLevels = [
  'Mudah',
  'Sedang',
  'Sulit'
];

const WorksheetManager = () => {
  const { success, error } = useToast();
  const { profileData } = useSimpleProfileData();
  const [worksheets, setWorksheets] = useState<Worksheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWorksheet, setEditingWorksheet] = useState<Worksheet | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState<Worksheet>({
    title: '',
    description: '',
    category: categories[0],
    age_range: ageRanges[0],
    difficulty_level: difficultyLevels[0],
    file_url: '',
    preview_image_url: '',
    file_size: '',
    page_count: 1,
    download_count: 0,
    rating: 0,
    is_featured: false,
    is_active: true,
    sort_order: 0
  });
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    loadWorksheets();
  }, []);

  const loadWorksheets = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('worksheets')
        .select('*')
        .order('sort_order', { ascending: true });

      if (fetchError) throw fetchError;
      setWorksheets(data || []);
    } catch (err) {
      console.error('Error loading worksheets:', err);
      error('Gagal memuat data worksheet');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof Worksheet, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUploadComplete = async (url: string, type: 'file' | 'image') => {
    if (type === 'file') {
      handleInputChange('file_url', url);
      // File size will be handled by FileUpload component
      
      // Generate thumbnail from PDF if no preview image is uploaded
      if (url.toLowerCase().endsWith('.pdf') && !formData.preview_image_url) {
        try {
          const thumbnailUrl = await generatePDFThumbnail(url);
          if (thumbnailUrl) {
            handleInputChange('preview_image_url', thumbnailUrl);
          }
        } catch (err) {
          console.error('Error generating PDF thumbnail:', err);
          // Set default PDF thumbnail if generation fails
          handleInputChange('preview_image_url', '/api/placeholder/300/200?text=PDF+Worksheet');
        }
      }
    } else {
      handleInputChange('preview_image_url', url);
    }
  };

  const generatePDFThumbnail = async (pdfUrl: string): Promise<string | null> => {
    try {
      // Use PDF.js from CDN for thumbnail generation
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      
      return new Promise((resolve) => {
        script.onload = async () => {
          try {
            // @ts-expect-error - PDF.js loaded from CDN
            const pdfjsLib = window.pdfjsLib;
            
            if (!pdfjsLib) {
              resolve(null);
              return;
            }
            
            // Set worker source
            pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            
            // Load PDF document
            const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
            const page = await pdf.getPage(1); // Get first page
            
            // Set up canvas
            const scale = 1.5;
            const viewport = page.getViewport({ scale });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            
            if (!context) {
              resolve(null);
              return;
            }
            
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            
            // Render page to canvas
            await page.render({
              canvasContext: context,
              viewport: viewport
            }).promise;
            
            // Convert canvas to blob and upload
            canvas.toBlob(async (blob) => {
              if (!blob) {
                resolve(null);
                return;
              }
              
              try {
                // Upload thumbnail to storage
                const fileName = `thumbnail-${Date.now()}-${Math.random().toString(36).substring(2)}.png`;
                const filePath = `worksheets/thumbnails/${fileName}`;
                
                const { error: uploadError } = await supabase.storage
                  .from('worksheet-files')
                  .upload(filePath, blob, {
                    cacheControl: '3600',
                    upsert: false
                  });
                
                if (uploadError) {
                  console.error('Upload error:', uploadError);
                  resolve(null);
                  return;
                }
                
                // Get public URL
                const { data } = supabase.storage
                  .from('worksheet-files')
                  .getPublicUrl(filePath);
                
                resolve(data.publicUrl);
              } catch (err) {
                console.error('Error uploading thumbnail:', err);
                resolve(null);
              }
            }, 'image/png', 0.8);
          } catch (err) {
            console.error('Error in PDF processing:', err);
            resolve(null);
          }
        };
        
        script.onerror = () => {
          console.error('Failed to load PDF.js');
          resolve(null);
        };
        
        document.head.appendChild(script);
      });
    } catch (err) {
      console.error('Error generating PDF thumbnail:', err);
      return null;
    }
  };

  const handleFileUploadError = (errorMessage: string) => {
    error(errorMessage);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (!profileData?.id) {
        throw new Error('Profile data not available');
      }

      const dataToSave = {
        ...formData,
        profile_id: profileData.id
      };

      if (editingWorksheet?.id) {
        const { error: updateError } = await supabase
          .from('worksheets')
          .update(dataToSave)
          .eq('id', editingWorksheet.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('worksheets')
          .insert([dataToSave]);

        if (insertError) throw insertError;
      }

      success('Worksheet berhasil disimpan!');
      setShowForm(false);
      setEditingWorksheet(null);
      resetForm();
      loadWorksheets();
    } catch (err) {
      console.error('Error saving worksheet:', err);
      error('Gagal menyimpan worksheet');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (worksheet: Worksheet) => {
    setEditingWorksheet(worksheet);
    setFormData(worksheet);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!id) {
      error('ID worksheet tidak valid');
      return;
    }
    
    if (!confirm('Apakah Anda yakin ingin menghapus worksheet ini?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('worksheets')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      
      success('Worksheet berhasil dihapus!');
      loadWorksheets();
    } catch (err) {
      console.error('Error deleting worksheet:', err);
      error('Gagal menghapus worksheet');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: categories[0],
      age_range: ageRanges[0],
      difficulty_level: difficultyLevels[0],
      file_url: '',
      preview_image_url: '',
      file_size: '',
      page_count: 1,
      download_count: 0,
      rating: 0,
      is_featured: false,
      is_active: true,
      sort_order: 0
    });
  };

  const filteredWorksheets = worksheets.filter(worksheet => {
    const matchesSearch = worksheet.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         worksheet.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || worksheet.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-300 rounded w-48"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-300 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Kelola Worksheet</h2>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingWorksheet(null);
              resetForm();
            }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Tambah Worksheet
          </button>
        </div>

        {/* Search and Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari worksheet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="all">Semua Kategori</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Worksheet List */}
        <div className="space-y-4">
          {filteredWorksheets.map((worksheet) => (
            <div key={worksheet.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{worksheet.title}</h3>
                    {worksheet.is_featured && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                        Featured
                      </span>
                    )}
                    {!worksheet.is_active && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                        Tidak Aktif
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{worksheet.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>Kategori: {worksheet.category || 'Tidak ada'}</span>
                    <span>Usia: {worksheet.age_range || 'Tidak ada'}</span>
                    <span>Level: {worksheet.difficulty_level || 'Tidak ada'}</span>
                    <span>Download: {worksheet.download_count || 0}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {worksheet.file_url && (
                    <a
                      href={worksheet.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-green-600 hover:text-green-800 transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => handleEdit(worksheet)}
                    className="p-2 text-blue-600 hover:text-blue-800 transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => worksheet.id && handleDelete(worksheet.id)}
                    className={`p-2 transition-colors ${
                      worksheet.id 
                        ? 'text-red-600 hover:text-red-800' 
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                    title={worksheet.id ? "Hapus" : "ID tidak tersedia"}
                    disabled={!worksheet.id}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filteredWorksheets.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {searchTerm || filterCategory !== 'all' ? 'Tidak ada worksheet yang sesuai dengan filter' : 'Belum ada worksheet'}
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingWorksheet ? 'Edit Worksheet' : 'Tambah Worksheet Baru'}
                </h3>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingWorksheet(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Judul *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Masukkan judul worksheet"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Deskripsi</label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Masukkan deskripsi worksheet"
                  />
                </div>

                {/* Category, Age Range, Difficulty */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Kategori</label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rentang Usia</label>
                    <select
                      value={formData.age_range}
                      onChange={(e) => handleInputChange('age_range', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {ageRanges.map(range => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tingkat Kesulitan</label>
                    <select
                      value={formData.difficulty_level}
                      onChange={(e) => handleInputChange('difficulty_level', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {difficultyLevels.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    File Worksheet (PDF) *
                  </label>
                  <FileUpload
                    onUploadComplete={(url) => handleFileUploadComplete(url, 'file')}
                    onUploadError={handleFileUploadError}
                    acceptedTypes={['application/pdf']}
                    maxSizeInMB={10}
                    bucket="portfolio-images"
                    folder="worksheets"
                    className="w-full"
                  />
                  {formData.file_url && (
                    <p className="text-sm text-green-600 mt-1">✓ File berhasil diupload</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Upload file PDF worksheet yang akan dibagikan</p>
                </div>

                {/* Preview Image Upload - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gambar Preview (opsional)
                  </label>
                  <ImageUpload
                    value={formData.preview_image_url}
                    onChange={(url) => handleFileUploadComplete(url, 'image')}
                    bucket="portfolio-images"
                    folder="worksheets"
                    maxSize={5}
                    placeholder="Upload gambar preview worksheet"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Gambar akan dibuat otomatis dari PDF jika tidak diupload</p>
                </div>

                {/* Optional Fields - Collapsed by default */}
                <div className="border-t pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center text-sm text-gray-600 hover:text-gray-800 mb-3"
                  >
                    <span>Pengaturan Lanjutan</span>
                    <span className="ml-1">{showAdvanced ? '▼' : '▶'}</span>
                  </button>
                  
                  {showAdvanced && (
                    <div className="space-y-4">
                      {/* Page Count */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Jumlah Halaman (opsional)</label>
                        <input
                          type="number"
                          min="1"
                          value={formData.page_count || ''}
                          onChange={(e) => handleInputChange('page_count', e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Akan dideteksi otomatis dari PDF"
                        />
                      </div>

                      {/* Sort Order */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Urutan Tampil (opsional)</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.sort_order || ''}
                          onChange={(e) => handleInputChange('sort_order', e.target.value ? parseInt(e.target.value) : 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0 (default)"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Checkboxes */}
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_featured"
                      checked={formData.is_featured}
                      onChange={(e) => handleInputChange('is_featured', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-900">
                      Featured (tampil di beranda)
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                      Aktif
                    </label>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingWorksheet(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Batal
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !formData.title.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorksheetManager;